import Globals from './framework/globals.js';
import AppModel from './careers/models/app-model.js';
import Utils from './framework/utils.js';
import JobPostView from './careers/views/job-post-view.js';
import FilterView from './careers/views/filter-view.js';
import PaginationView from './careers/views/pagination-view.js';
import MapView from './careers/views/map-view.js';

// FBHSCareers
// ===========
//
// Manages the Careers Map block. Pulls job post data from a SmartRecruiters feed and location data from WP.
// The map locations are from WP, but filtered down to only locations with job posts. Association is made on
// the site_name property from SR.

(() => {
  class FBHSCareers extends EventTarget {
    constructor(elem) {
      super();

      // set up a few global variables to be used elsewhere
      Globals.set('customFieldMap', {
        siteName: '5fbbf701e9960d79739a3e0a',
        brand: '5f5b6cb1772192746bce793a'
      });

      Utils.bindMethods(this, [
        'onModelReady',
        'onFilterChange', 'onFilterSubmit', 'onFilterReset',
        'onPaginationLast', 'onPaginationNext', 'onPaginationFirst', 'onPaginationSelect', 'onPaginationPrevious', 'onPaginationChange',
        'onLocationRequest'
      ]);

      this.elem = elem;
      this.resultViews = [];
      this.model = new AppModel({
        gmapsApiKey: window.gmapi
      });
      this.model.addEventListener(AppModel.Events.READY, this.onModelReady);
      this.model.init();
    }

    onModelReady(evt) {
      // once the model has loaded all necessary data, we can start our setup
      this.renderData();
    }

    renderData() {
      let template = document.querySelector('.tmpl-result-row').content;

      // create a collection of job post views for the table
      this.model.get('posts').forEach((post) => {
        let view = new JobPostView(template.cloneNode(true), post);
        this.resultViews.push(view);
      })

      // build the map view
      if (this.model.get('gmapsApiKey')) {
        this.mapView = new MapView(this.elem.querySelector('.map'), this.model);
        this.mapView.addEventListener(MapView.Events.LOCATION_REQUEST, this.onLocationRequest);
      }

      // build the filter view and listen for relevant events
      this.filterView = new FilterView(this.elem.querySelector('.careers-table_filter-form'), this.model.get('filterModel'));
      this.filterView.addEventListener(FilterView.Events.CHANGE, this.onFilterChange);
      this.filterView.addEventListener(FilterView.Events.SUBMIT, this.onFilterSubmit);
      this.filterView.addEventListener(FilterView.Events.RESET, this.onFilterReset);

      // build the pagination view and listen for relevant events
      this.paginationView = new PaginationView(this.elem.querySelector('.careers-table_pagination'), document.querySelector('.tmpl-pagination-page').content, this.model.get('paginationModel'));
      this.paginationView.addEventListener(PaginationView.Events.SELECT, this.onPaginationSelect);
      this.paginationView.addEventListener(PaginationView.Events.PREVIOUS, this.onPaginationPrevious);
      this.paginationView.addEventListener(PaginationView.Events.NEXT, this.onPaginationNext);
      this.paginationView.addEventListener(PaginationView.Events.FIRST, this.onPaginationFirst);
      this.paginationView.addEventListener(PaginationView.Events.LAST, this.onPaginationLast);

      // display the initial page of results
      this.displayResults();
      // update the results table whenever there's a change
      this.model.get('paginationModel').addEventListener('Model.Change', this.onPaginationChange);
    }

    displayResults() {
      if (!this.model.get('paginationModel.totalItems')) {
        this.elem.querySelector('.careers-table_table').classList.add('d-none');
        this.elem.querySelector('.careers-table_no-results').classList.remove('d-none');
        this.elem.querySelector('.careers-table_pagination').classList.add('d-none');
        this.elem.querySelector('.careers-table_table tbody').innerHTML = '';
      } else {
        this.elem.querySelector('.careers-table_table').classList.remove('d-none');
        this.elem.querySelector('.careers-table_no-results').classList.add('d-none');
        this.elem.querySelector('.careers-table_pagination').classList.remove('d-none');
        // trigger screen reader announcements
        let updates = [
          { key: '.careers-results_announcement_count', value: this.model.get('paginationModel.totalItems') },
          { key: '.careers-results_announcement_page-current', value: this.model.get('paginationModel.currentPage') },
          { key: '.careers-results_announcement_page-total', value: this.model.get('paginationModel').getPageCount() }
        ];
        updates.forEach((kvp) => {
          let e = this.elem.querySelector(kvp.key);
          e.innerHTML = kvp.value.toString() +  (e.innerHTML == kvp.value.toString() ? ',' : '');
        });
        
        // update table data
        let displayRange = this.model.get('paginationModel').getCurrentRange();
        let displayIndex = 0;
        let filteredPosts = this.model.get('posts').filter((a) => a.get('filterMatch'));
        this.elem.querySelector('.careers-table_table tbody').innerHTML = '';
        filteredPosts.forEach((post) => {
          if (displayIndex >= displayRange.start && displayIndex < displayRange.end) {
            this.elem.querySelector('.careers-table_table tbody').append(this.resultViews.find((a) => a.id == post.get('id')).dom.cloneNode(true));
          }
          displayIndex++;
        });

        // update map pins to filtered posts' locations
        let locationIds = [...new Set(filteredPosts.map(m => m.get('jobLocation.id')))];
        this.mapView.onFilter(locationIds);
      }
    }

    onFilterSubmit(evt) {
      this.model.get('filterModel').set('currentFilter', this.model.get('filterModel.selectedFilter'));
    }

    onFilterReset(evt) {
      this.model.get('filterModel').reset();
    }

    onFilterChange(evt) {
      this.model.get('filterModel').selectFilter(evt.data, this.model.get('posts'));
    }

    onPaginationLast(evt) {
      this.model.get('paginationModel').lastPage();
    }
    onPaginationNext(evt) {
      this.model.get('paginationModel').nextPage();
    }
    onPaginationFirst(evt) {
      this.model.get('paginationModel').firstPage();
    }
    onPaginationSelect(evt) {
      this.model.get('paginationModel').set('currentPage', evt.data.page);
    }
    onPaginationPrevious(evt) {
      this.model.get('paginationModel').previousPage();
    }

    onPaginationChange(evt) {
      if (evt.data.path == 'currentPage') {
        this.displayResults();
      }
    }

    onLocationRequest(evt) {
      // when a location on the map is clicked, apply the appropriate filter and scroll to results
      this.model.get('filterModel').setLocationById(evt.data.location_id, this.model.get('posts'));

      let scrollY = window.pageYOffset || doucment.docuemntElement.scrollTop;
      window.scroll({
        top: this.filterView.dom.getBoundingClientRect().top + scrollY,
        behavior: 'smooth'
      })
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    // works under the assumption that there's only one careers map per page. Multiple maps have not been tested
    let mapdom = document.querySelector('.fbhs-careers-map');
    if (mapdom) {
      let careerMap = new FBHSCareers(mapdom);
    }
  })
})();